// v4 - td-portal + measured pixel sizing (no percentages, no coordinate math)
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Overlays.css";

/**
 * InCellEditor — Excel-style in-cell editing overlay (ExcelWebEdit).
 *
 * The <input> is portaled INTO the selected cell's <td> and sized with the
 * td's own measured offsetWidth/offsetHeight (same layout/zoom context, so
 * no unit conversion is ever needed). Percentage sizing proved unreliable
 * here: an <input> is a replaced element and its percentage width resolves
 * against the containing block, which depends on positioning timing - the
 * measured pixels remove that whole class of problem.
 *
 * Enter or blur commits through onCommit(row, col, text); Escape cancels.
 * Selection and row data are read through refs refreshed on every render
 * because the DOM listeners survive across renders.
 */
export default function InCellEditor({
  active, gridWrapperRef, selectedCell, rows, formulas, onCommit, onNavigate,
  onDeleteSelection, // () => void — Delete/Backspace clears the WHOLE selection
}) {
  // { td, width, height } - null when the editor is closed.
  const [edit, setEdit] = useState(null);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  const cellRef = useRef({ row: 0, col: 0 });
  const originalRef = useRef("");
  const restoreRef = useRef(null); // { td, position, inner }

  const selRef = useRef(selectedCell);
  selRef.current = selectedCell;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const formulasRef = useRef(formulas);
  formulasRef.current = formulas;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const onDeleteSelectionRef = useRef(onDeleteSelection);
  onDeleteSelectionRef.current = onDeleteSelection;
  // Mirror of `edit` for the window keydown listener (stale-closure guard).
  const editOpenRef = useRef(false);

  const closeEditor = useCallback(() => {
    const r = restoreRef.current;
    if (r) {
      r.td.style.position = r.position;
      if (r.inner) r.inner.style.visibility = "";
      restoreRef.current = null;
    }
    editOpenRef.current = false;
    setEdit(null);
  }, []);

  const openEditor = useCallback((typedChar) => {
    const wrap = gridWrapperRef.current;
    if (!wrap) return;
    const td = wrap.querySelector("td.sev-sel-active");
    if (!td) return;

    const { row, col } = selRef.current;
    cellRef.current = { row, col };
    // Edit the FORMULA text when the cell holds one (Excel behaviour);
    // otherwise edit the displayed value.
    const fkey = `${row}:${col}`;
    const formula = formulasRef.current && formulasRef.current[fkey];
    const raw = formula != null ? formula : rowsRef.current?.[row]?.[col];
    originalRef.current = raw == null ? "" : String(raw);
    // Typing a printable char on a selected cell REPLACES its content and
    // starts editing (Excel). F2 / Enter / double-click edit the existing
    // value with the cursor at the end.
    setDraft(typedChar != null ? typedChar : (raw == null ? "" : String(raw)));

    // Make the td the positioning context SYNCHRONOUSLY (before the portal
    // renders) and hide its content while editing - long text overflows
    // into the neighbouring cell and the tail would stick out beside the
    // input.
    const prevPosition = td.style.position;
    if (getComputedStyle(td).position === "static") {
      td.style.position = "relative";
    }
    const inner = td.querySelector(".simple-excel-cell");
    if (inner) inner.style.visibility = "hidden";
    restoreRef.current = { td, position: prevPosition, inner };

    // Measured layout pixels - the input renders in the SAME context.
    const width = td.offsetWidth;
    const height = td.offsetHeight;
    // eslint-disable-next-line no-console
    console.info("[ExcelWebEdit] in-cell editor open", {
      cell: `${row}:${col}`, tdWidth: width, tdHeight: height,
    });
    editOpenRef.current = true;
    setEdit({ td, width, height });
  }, [gridWrapperRef]);

  // Open on double-click or F2 while edit mode is active.
  useEffect(() => {
    if (!active) {
      closeEditor();
      return undefined;
    }
    const wrap = gridWrapperRef.current;
    if (!wrap) return undefined;
    const onDblClick = (e) => {
      if (e.target.closest && e.target.closest("td")) openEditor();
    };
    const onKeyDown = (e) => {
      if (e.key === "F2") {
        openEditor();
        return;
      }
      // Excel behaviour: Enter on a selected cell starts editing it.
      // Ignore Enter aimed at form controls (formula bar, buttons, menus).
      const tag = String(e.target.tagName || "").toLowerCase();
      const onTextField = tag === "input" || tag === "textarea" || tag === "select";
      const onButton = tag === "button";
      if (e.key === "Enter" && !editOpenRef.current) {
        if (onTextField || onButton) return;
        e.preventDefault();
        openEditor();
        return;
      }
      // Excel behaviour: Delete / Backspace on a selected cell CLEARS its
      // content (QA #1) without opening the editor.
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        !editOpenRef.current &&
        !onTextField &&
        !e.ctrlKey && !e.metaKey && !e.altKey
      ) {
        e.preventDefault();
        // Excel clears EVERY selected cell (range / column / row), not just
        // the active one (QA: data not deleted for a selected range).
        if (typeof onDeleteSelectionRef.current === "function") {
          onDeleteSelectionRef.current();
          return;
        }
        const { row, col } = selRef.current;
        onCommitRef.current(row, col, "");
        return;
      }
      // Excel behaviour: typing a printable character on a selected cell
      // starts editing and REPLACES the content with that character.
      // NOTE: focus resting on a BUTTON (e.g. a just-clicked sheet tab or
      // toolbar button) must not swallow typing (QA #7) — only real text
      // fields keep their keystrokes. Space still activates the button.
      if (
        !editOpenRef.current &&
        !onTextField &&
        !(onButton && e.key === " ") &&
        e.key.length === 1 &&
        !e.ctrlKey && !e.metaKey && !e.altKey
      ) {
        e.preventDefault();
        openEditor(e.key);
      }
    };
    wrap.addEventListener("dblclick", onDblClick);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      wrap.removeEventListener("dblclick", onDblClick);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [active, gridWrapperRef, openEditor, closeEditor]);

  // Focus the input as soon as it appears, then verify the geometry so any
  // residual mismatch is visible in the console instead of being guessed at.
  useEffect(() => {
    if (edit && inputRef.current) {
      const el = inputRef.current;
      el.focus({ preventScroll: true });
      // Place the caret at the END (Excel puts the cursor at the end on
      // F2/Enter) rather than selecting the whole value.
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch (e) { /* ignore */ }
      const tdRect = edit.td.getBoundingClientRect();
      const inRect = inputRef.current.getBoundingClientRect();
      if (Math.abs(tdRect.width - inRect.width) > 4 || Math.abs(tdRect.left - inRect.left) > 4) {
        // eslint-disable-next-line no-console
        console.warn("[ExcelWebEdit] editor geometry mismatch", {
          td: { left: tdRect.left, top: tdRect.top, width: tdRect.width, height: tdRect.height },
          input: { left: inRect.left, top: inRect.top, width: inRect.width, height: inRect.height },
        });
      }
    }
  }, [edit]);

  if (!active || !edit || !edit.td.isConnected) return null;

  const commit = () => {
    // The value was already applied live on each keystroke; just finalise.
    onCommitRef.current(cellRef.current.row, cellRef.current.col, draft);
    closeEditor();
  };

  // Escape reverts the live edits back to the cell's original content.
  const cancelEdit = () => {
    onCommitRef.current(cellRef.current.row, cellRef.current.col, originalRef.current);
    closeEditor();
  };

  // Excel behaviour: Enter commits and moves down (Shift+Enter up),
  // Tab commits and moves right (Shift+Tab left).
  const commitAndMove = (dRow, dCol) => {
    commit();
    if (onNavigateRef.current) onNavigateRef.current(dRow, dCol);
  };

  return createPortal(
    <input
      ref={inputRef}
      type="text"
      className="sev-incell-editor"
      data-allow-text-selection="true"
      style={{
        top: "-1px",
        left: "-1px",
        // Sizing goes through CSS variables: signin.css declares
        // `input { width: 50px !important }` globally, which overrides
        // inline width - but not a higher-specificity !important rule
        // reading these variables (see Overlays.css).
        "--xlwe-w": `${edit.width + 2}px`,
        "--xlwe-h": `${edit.height + 2}px`,
      }}
      value={draft}
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        // Live-commit each keystroke so the model updates and the formula
        // bar mirrors what you type in the cell (two-way sync).
        onCommitRef.current(cellRef.current.row, cellRef.current.col, text);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commitAndMove(e.shiftKey ? -1 : 1, 0);
        } else if (e.key === "Tab") {
          e.preventDefault();
          commitAndMove(0, e.shiftKey ? -1 : 1);
        } else if (e.key === "Escape") {
          cancelEdit();
        } else if (
          (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
          String(e.key).toLowerCase() === "z"
        ) {
          // Ctrl+Z while editing a cell reverts the live typing (Excel).
          e.preventDefault();
          cancelEdit();
        }
        // Keep grid keyboard navigation and shortcuts out of the input.
        e.stopPropagation();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    />,
    edit.td
  );
}
